const BUTTON_SELECTOR = 'button.btn-primary';
const DIALOG_SELECTOR = 'dialog, [role="dialog"], [role="alertdialog"], .modal-overlay, .modal-box';

/** Todo `button.btn-primary` que NO este dentro de un modal/dialogo (decision 2026-10-24: antes solo lo recibian las
 *  rutas de modulo y se quedaban fuera inicio, mi cuenta y no encontrado). No usa lista de rutas. Sin envolver nodos de
 *  React ni interceptar la activacion. */
export function attachMagneticButtons(root) {
  if (!root) return () => {};
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const mouse = matchMedia('(hover: hover) and (pointer: fine)');
  const buttons = new Set();
  let active = null;
  let x = 0, y = 0, goalX = 0, goalY = 0, frame = 0, previous = 0;

  function eligible(button) {
    return root.contains(button) && button.matches(BUTTON_SELECTOR) && !button.closest(DIALOG_SELECTOR);
  }

  function available(button) {
    return eligible(button) && !button.disabled && button.getAttribute('aria-busy') !== 'true'
      && !(document.activeElement === button && button.matches(':focus-visible'));
  }

  function paint() {
    if (!active) return;
    active.style.setProperty('--magnet-x', `${x.toFixed(3)}px`);
    active.style.setProperty('--magnet-y', `${y.toFixed(3)}px`);
  }

  function reset() {
    cancelAnimationFrame(frame);
    frame = previous = 0;
    x = y = goalX = goalY = 0;
    if (active) {
      active.style.removeProperty('--magnet-x');
      active.style.removeProperty('--magnet-y');
    }
    active = null;
  }

  function tick(time) {
    if (!active || !available(active) || reduced.matches || !mouse.matches) { reset(); return; }
    const dt = previous ? Math.min(time - previous, 40) : 16.67;
    previous = time;
    const blend = 1 - Math.exp(-dt / 65);
    x += (goalX - x) * blend;
    y += (goalY - y) * blend;
    if (Math.abs(goalX - x) + Math.abs(goalY - y) < .015) {
      x = goalX; y = goalY;
      paint(); frame = previous = 0;
      return;
    }
    paint();
    frame = requestAnimationFrame(tick);
  }

  function moveTo(nextX, nextY) {
    goalX = nextX; goalY = nextY;
    if (active && !frame) frame = requestAnimationFrame(tick);
  }

  function track(event) {
    if (event.pointerType !== 'mouse' || reduced.matches || !mouse.matches) { reset(); return; }
    // A pointer over an overlay must never activate an underlying module action.
    if (event.target.closest(DIALOG_SELECTOR)) { reset(); return; }
    let selected = null;
    let deltaX = 0, deltaY = 0;
    for (const button of buttons) {
      if (!available(button) || !button.getClientRects().length) continue;
      const rect = button.getBoundingClientRect();
      // Translation and press scale are independent. Recover the stable layout center.
      const dx = event.clientX - (rect.x + rect.width / 2 - (button === active ? x : 0));
      const dy = event.clientY - (rect.y + rect.height / 2 - (button === active ? y : 0));
      const scale = button === active ? parseFloat(getComputedStyle(button).scale) || 1 : 1;
      if (Math.abs(dx) <= rect.width / scale / 2 + 12 && Math.abs(dy) <= rect.height / scale / 2 + 12) {
        selected = button; deltaX = dx; deltaY = dy;
        if (button.contains(event.target)) break;
      }
    }
    if (!selected) { moveTo(0, 0); return; }
    if (active !== selected) { reset(); active = selected; }
    moveTo(Math.max(-8, Math.min(8, deltaX * .24)), Math.max(-5, Math.min(5, deltaY * .24)));
  }

  function scan() {
    for (const button of buttons) {
      if (eligible(button)) {
        if (!button.classList.contains('module-magnetic')) button.classList.add('module-magnetic');
        continue;
      }
      if (button === active) reset();
      button.classList.remove('module-magnetic');
      buttons.delete(button);
    }
    for (const button of root.querySelectorAll(BUTTON_SELECTOR)) {
      if (eligible(button) && !buttons.has(button)) {
        buttons.add(button);
        button.classList.add('module-magnetic');
      }
    }
    if (active && !available(active)) reset();
  }

  function focus(event) {
    if (event.target.matches(`${BUTTON_SELECTOR}:focus-visible`)) reset();
  }
  function keyboard(event) {
    if (['Tab', 'Enter', ' '].includes(event.key)) reset();
  }
  function visibility() { if (document.hidden) reset(); }
  function leave() { moveTo(0, 0); }
  const observer = new MutationObserver(scan);
  scan();
  observer.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'disabled', 'aria-busy', 'role'] });
  root.addEventListener('pointermove', track);
  root.addEventListener('pointerleave', leave);
  root.addEventListener('pointercancel', reset);
  root.addEventListener('focusin', focus);
  root.addEventListener('keydown', keyboard);
  window.addEventListener('blur', reset);
  window.addEventListener('resize', reset);
  window.addEventListener('scroll', reset, true);
  document.addEventListener('visibilitychange', visibility);
  reduced.addEventListener('change', reset);
  mouse.addEventListener('change', reset);
  return () => {
    observer.disconnect();
    reset();
    for (const button of buttons) button.classList.remove('module-magnetic');
    root.removeEventListener('pointermove', track);
    root.removeEventListener('pointerleave', leave);
    root.removeEventListener('pointercancel', reset);
    root.removeEventListener('focusin', focus);
    root.removeEventListener('keydown', keyboard);
    window.removeEventListener('blur', reset);
    window.removeEventListener('resize', reset);
    window.removeEventListener('scroll', reset, true);
    document.removeEventListener('visibilitychange', visibility);
    reduced.removeEventListener('change', reset);
    mouse.removeEventListener('change', reset);
  };
}
